// frontend/src/pages/ControleCpfsPage/ControleCpfsPage.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Typography, Table, message, Tag, Tooltip, List, Card, Modal, Form, Select,
    DatePicker, Input, Button, Row, Col, Popover, Space, Statistic, Checkbox, Popconfirm,
} from 'antd';
import {
    UserOutlined, WarningOutlined, PlusOutlined, CalendarOutlined,
    InfoCircleOutlined, ExclamationCircleOutlined, DeleteOutlined, UnorderedListOutlined,
} from '@ant-design/icons';
import * as milhasService from '../../services/milhasService';
import dayjs from 'dayjs';
import './ControleCpfsPage.css';
import { getErrorMessage } from '../../utils/errorHandler';
import EmptyStateGuide from '../../components/EmptyStateGuide/EmptyStateGuide';

const { Title, Text } = Typography;

const maskCpf = (cpf) => {
    const digits = String(cpf || '').replace(/\D/g, '');
    if (digits.length !== 11) return cpf || '—';
    return `${digits.slice(0, 3)}.***.***-${digits.slice(9)}`;
};

const formatSaldo = (valor) => {
    const n = Number(valor || 0);
    return n.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
};

const periodoLabel = (politica) => {
    if (politica === 'ANO_ROLANTE') return 'Ano-Rolante (365 dias)';
    if (politica === 'LISTA_BENEFICIARIOS') return 'Lista de Beneficiários';
    if (politica === 'APENAS_MONITORAR') return 'Apenas monitorar';
    return 'Ano-Calendário';
};

const periodoDetalhe = (item) => {
    if (item.politica_reset_cpf === 'LISTA_BENEFICIARIOS') {
        return 'Lista de Beneficiários';
    }
    if (item.periodo_inicio && item.periodo_fim) {
        const ini = dayjs(item.periodo_inicio).format('DD/MM/YYYY');
        const fim = dayjs(item.periodo_fim).format('DD/MM/YYYY');
        if (item.politica_reset_cpf === 'ANO_ROLANTE') {
            return `Últimos 365 dias (${ini} – ${fim})`;
        }
        return `${periodoLabel(item.politica_reset_cpf)} ${dayjs(item.periodo_inicio).year()} (${ini} – ${fim})`;
    }
    return periodoLabel(item.politica_reset_cpf);
};

const DualCpfBar = ({ usados, limite, passageiros, showDetailIcon = true }) => {
    const semLimite = !limite || limite === 0;
    if (semLimite) {
        return (
            <Space size={8} wrap>
                <Tag color="default">Apenas monitorar · {usados} usados</Tag>
                {showDetailIcon && usados > 0 && (
                    <PassageirosPopover passageiros={passageiros} />
                )}
            </Space>
        );
    }

    const usadosNum = Math.max(0, Number(usados) || 0);
    const limiteNum = Math.max(0, Number(limite) || 0);
    const restantesVisuais = Math.max(0, limiteNum - usadosNum);
    // Se ultrapassou o limite, a barra fica 100% vermelha (como no print 15/5).
    const usedFlex = usadosNum >= limiteNum ? Math.max(usadosNum, 1) : Math.max(usadosNum, 0);
    const restFlex = usadosNum >= limiteNum ? 0 : Math.max(restantesVisuais, 0);
    const percentUso = limiteNum > 0 ? (usadosNum / limiteNum) * 100 : 0;
    const isCritical = percentUso >= 100;
    const isWarning = percentUso >= 80 && percentUso < 100;
    const showUsedLabel = usedFlex > 0 && (usadosNum >= limiteNum || usadosNum / limiteNum >= 0.12 || usadosNum >= 2);
    const showRestLabel = restFlex > 0 && (restantesVisuais / limiteNum >= 0.12 || restantesVisuais >= 2);

    return (
        <div className={`cpf-dual-wrap${isCritical ? ' cpf-dual-wrap--critical' : ''}${isWarning ? ' cpf-dual-wrap--warning' : ''}`}>
            <div
                className="cpf-dual-bar"
                role="img"
                aria-label={`${usadosNum} usados, ${restantesVisuais} disponíveis de ${limiteNum}`}
                title={`Usados: ${usadosNum} · Disponíveis: ${restantesVisuais} · Limite: ${limiteNum}`}
            >
                {(usedFlex > 0 || usadosNum > 0) && (
                    <div
                        className="cpf-dual-seg cpf-dual-seg--used"
                        style={{ flexGrow: Math.max(usedFlex, usadosNum > 0 ? usadosNum : 0), flexBasis: 0 }}
                    >
                        {showUsedLabel ? <span>{usadosNum}</span> : null}
                    </div>
                )}
                {restFlex > 0 && (
                    <div
                        className="cpf-dual-seg cpf-dual-seg--rest"
                        style={{ flexGrow: restFlex, flexBasis: 0 }}
                    >
                        {showRestLabel ? <span>{restantesVisuais}</span> : null}
                    </div>
                )}
                {usadosNum === 0 && restFlex === 0 && (
                    <div className="cpf-dual-seg cpf-dual-seg--rest" style={{ flexGrow: 1, flexBasis: 0 }}>
                        <span>0</span>
                    </div>
                )}
            </div>
            <div className="cpf-dual-meta">
                <Tag
                    className="cpf-dual-badge"
                    color={isCritical ? 'error' : isWarning ? 'warning' : 'default'}
                >
                    {usadosNum}/{limiteNum}
                </Tag>
                {showDetailIcon && (
                    <PassageirosPopover passageiros={passageiros} />
                )}
                {isCritical && (
                    <Tooltip title="Limite de CPFs atingido ou ultrapassado">
                        <WarningOutlined style={{ color: '#ff4d4f' }} />
                    </Tooltip>
                )}
            </div>
        </div>
    );
};

const PassageirosPopover = ({ passageiros }) => {
    const lista = Array.isArray(passageiros) ? passageiros : [];
    const content = lista.length === 0 ? (
        <Text type="secondary">Nenhum CPF de terceiro registrado neste período.</Text>
    ) : (
        <ul className="cpf-passageiros-list">
            {lista.map((p, idx) => {
                const cpf = typeof p === 'string' ? p : p?.cpf;
                const nome = typeof p === 'string' ? null : p?.nome;
                const desconhecido = typeof p === 'object' && p?.cpf_desconhecido;
                return (
                    <li key={desconhecido ? `unk-${p?.uso_id || idx}` : (cpf || nome || idx)}>
                        <Text strong>{nome || 'Sem nome'}</Text>
                        <Text type="secondary" style={{ marginLeft: 8 }}>
                            {desconhecido ? 'CPF não informado' : maskCpf(cpf)}
                        </Text>
                    </li>
                );
            })}
        </ul>
    );

    return (
        <Popover
            title={`CPFs usados (${lista.length})`}
            content={content}
            trigger="click"
            placement="bottomLeft"
        >
            <Button
                type="text"
                size="small"
                icon={<CalendarOutlined style={{ color: '#50B0A0' }} />}
                aria-label="Ver CPFs usados"
            />
        </Popover>
    );
};

const ControleCpfsPage = () => {
    const navigate = useNavigate();
    const [cpfData, setCpfData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [passageiros, setPassageiros] = useState([]);
    const [modalInformarCpfVisible, setModalInformarCpfVisible] = useState(false);
    const [recordParaInformar, setRecordParaInformar] = useState(null);
    const [modalUsosVisible, setModalUsosVisible] = useState(false);
    const [recordUsos, setRecordUsos] = useState(null);
    const [usosInformados, setUsosInformados] = useState([]);
    const [loadingUsos, setLoadingUsos] = useState(false);
    const [filtroPessoa, setFiltroPessoa] = useState('todos');
    const [filtroPrograma, setFiltroPrograma] = useState('todos');
    const [form] = Form.useForm();
    const [messageApi, contextHolder] = message.useMessage();
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const cpfDesconhecidoWatch = Form.useWatch('cpf_desconhecido', form);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [resCpf, resPass] = await Promise.all([
                milhasService.getControleCpfs(),
                milhasService.getPassageiros(),
            ]);
            const data = resCpf?.data ?? [];
            setCpfData(Array.isArray(data) ? data : []);
            setPassageiros(resPass?.data ?? []);
        } catch (error) {
            messageApi.error(getErrorMessage(error, 'Erro ao buscar dados de controle de CPFs.'));
            console.error('Erro no fetch de CPFs:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [messageApi]);

    const abrirModalInformarCpf = (record) => {
        setRecordParaInformar(record);
        form.resetFields();
        form.setFieldsValue({
            programa_portador: record.programa_portador_id,
            data_emissao: dayjs(),
            tipo_passageiro: 'existente',
            cpf_desconhecido: false,
        });
        setModalInformarCpfVisible(true);
    };

    const fecharModalInformarCpf = () => {
        setModalInformarCpfVisible(false);
        setRecordParaInformar(null);
        form.resetFields();
    };

    const carregarUsosInformados = async (programaPortadorId) => {
        setLoadingUsos(true);
        try {
            const res = await milhasService.getUsosCpfInformados(programaPortadorId);
            setUsosInformados(Array.isArray(res?.data) ? res.data : []);
        } catch (error) {
            messageApi.error(getErrorMessage(error, 'Erro ao listar usos informados.'));
            setUsosInformados([]);
        } finally {
            setLoadingUsos(false);
        }
    };

    const abrirModalUsos = (record) => {
        setRecordUsos(record);
        setModalUsosVisible(true);
        carregarUsosInformados(record.programa_portador_id);
    };

    const fecharModalUsos = () => {
        setModalUsosVisible(false);
        setRecordUsos(null);
        setUsosInformados([]);
    };

    const handleExcluirUso = async (usoId) => {
        try {
            await milhasService.deleteUsoCpfInformado(usoId);
            messageApi.success('Registro removido.');
            if (recordUsos?.programa_portador_id) {
                await carregarUsosInformados(recordUsos.programa_portador_id);
            }
            fetchData();
        } catch (error) {
            messageApi.error(getErrorMessage(error, 'Erro ao remover registro.'));
        }
    };

    const handleSubmitInformarCpf = async () => {
        try {
            const values = await form.validateFields();
            const payload = {
                programa_portador: recordParaInformar?.programa_portador_id,
                data_emissao: values.data_emissao?.format('YYYY-MM-DD'),
                observacao: values.observacao || '',
            };
            if (values.cpf_desconhecido) {
                payload.cpf_desconhecido = true;
                payload.nome_referencia = (values.nome_referencia || values.nome_completo || '').trim();
            } else if (values.tipo_passageiro === 'existente' && values.passageiro_id) {
                payload.passageiro_id = values.passageiro_id;
            } else {
                payload.nome_completo = values.nome_completo;
                payload.cpf = (values.cpf || '').replace(/\D/g, '');
            }
            await milhasService.createUsoCpfInformado(payload);
            messageApi.success(
                values.cpf_desconhecido
                    ? 'Uso sem CPF registrado. Cada registro consome 1 vaga na contagem.'
                    : 'Uso de CPF informado registrado com sucesso.'
            );
            fecharModalInformarCpf();
            fetchData();
        } catch (error) {
            if (error.errorFields) return;
            messageApi.error(getErrorMessage(error, 'Erro ao registrar uso de CPF.'));
        }
    };

    const pessoasOptions = useMemo(() => {
        const names = [...new Set(cpfData.map((i) => i.portador_nome).filter(Boolean))].sort();
        return names.map((n) => ({ value: n, label: n }));
    }, [cpfData]);

    const programasOptions = useMemo(() => {
        const names = [...new Set(cpfData.map((i) => i.programa_nome).filter(Boolean))].sort();
        return names.map((n) => ({ value: n, label: n }));
    }, [cpfData]);

    const filteredData = useMemo(() => {
        return cpfData.filter((item) => {
            if (filtroPessoa !== 'todos' && item.portador_nome !== filtroPessoa) return false;
            if (filtroPrograma !== 'todos' && item.programa_nome !== filtroPrograma) return false;
            return true;
        });
    }, [cpfData, filtroPessoa, filtroPrograma]);

    const summary = useMemo(() => {
        const comLimite = filteredData.filter((i) => i.limite_cpfs > 0);
        const usados = comLimite.reduce((acc, i) => acc + (i.cpfs_usados || 0), 0);
        const disponiveis = comLimite.reduce((acc, i) => acc + Math.max(0, i.cpfs_restantes ?? 0), 0);
        const noLimite = comLimite.filter((i) => (i.cpfs_usados || 0) >= i.limite_cpfs).length;
        const emAlerta = comLimite.filter((i) => {
            const pct = (i.cpfs_usados / i.limite_cpfs) * 100;
            return pct >= 80 && pct < 100;
        }).length;
        const saldoTotal = filteredData.reduce((acc, i) => acc + Number(i.saldo_atual || 0), 0);
        return { usados, disponiveis, noLimite, emAlerta, saldoTotal, contasComLimite: comLimite.length };
    }, [filteredData]);

    const groupedByPrograma = useMemo(() => {
        const map = new Map();
        filteredData.forEach((item) => {
            const key = item.programa_nome || 'Programa';
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(item);
        });
        const groups = [...map.entries()].map(([programa, items]) => {
            const sorted = [...items].sort((a, b) => {
                const aSem = !a.limite_cpfs;
                const bSem = !b.limite_cpfs;
                if (aSem !== bSem) return aSem ? 1 : -1;
                const restA = a.limite_cpfs ? (a.cpfs_restantes ?? 0) : Number.POSITIVE_INFINITY;
                const restB = b.limite_cpfs ? (b.cpfs_restantes ?? 0) : Number.POSITIVE_INFINITY;
                if (restA !== restB) return restA - restB;
                return (a.portador_nome || '').localeCompare(b.portador_nome || '');
            });
            return { programa, items: sorted, sample: sorted[0] };
        });
        return groups.sort((a, b) => a.programa.localeCompare(b.programa));
    }, [filteredData]);

    const groupColumns = [
        {
            title: 'Pessoa',
            dataIndex: 'portador_nome',
            key: 'portador_nome',
            width: 180,
        },
        {
            title: 'CPFs',
            key: 'cpfs',
            width: 360,
            render: (_, record) => (
                <DualCpfBar
                    usados={record.cpfs_usados || 0}
                    limite={record.limite_cpfs || 0}
                    passageiros={record.passageiros}
                />
            ),
        },
        {
            title: 'Saldo',
            dataIndex: 'saldo_atual',
            key: 'saldo_atual',
            width: 120,
            align: 'right',
            render: (val) => <Text strong>{formatSaldo(val)}</Text>,
        },
        {
            title: 'Ações',
            key: 'acoes',
            width: 220,
            align: 'center',
            render: (_, record) => {
                if (record.politica_reset_cpf === 'LISTA_BENEFICIARIOS') return null;
                return (
                    <Space size={0} wrap>
                        <Tooltip title="Informar CPF utilizado em emissão anterior ao uso do sistema">
                            <Button
                                type="link"
                                size="small"
                                icon={<PlusOutlined />}
                                onClick={() => abrirModalInformarCpf(record)}
                            >
                                Informar CPF
                            </Button>
                        </Tooltip>
                        <Tooltip title="Ver e gerenciar usos informados">
                            <Button
                                type="link"
                                size="small"
                                icon={<UnorderedListOutlined />}
                                onClick={() => abrirModalUsos(record)}
                            >
                                Usos
                            </Button>
                        </Tooltip>
                    </Space>
                );
            },
        },
    ];

    const emptyCpfs = (
        <EmptyStateGuide
            title="Nenhum programa para monitorar."
            description="Ative um programa em Gerenciar Portadores (botão Ativar programa na linha da pessoa) para acompanhar o limite de CPFs usados em emissões."
            actionLabel="Ir para Portadores"
            onAction={() => navigate('/milhas/portadores')}
            icon={null}
        />
    );

    const renderMobileItem = (item) => {
        const limiteAtingido = item.limite_cpfs > 0 && item.cpfs_usados >= item.limite_cpfs;
        return (
            <Card
                key={item.programa_portador_id}
                className={`controle-cpfs-person-card${limiteAtingido ? ' controle-cpfs-person-card--critical' : ''}`}
                size="small"
            >
                <div className="controle-cpfs-card-header">
                    <Text strong style={{ fontSize: '1.05rem' }}>{item.portador_nome}</Text>
                    {limiteAtingido && (
                        <Tooltip title="Limite atingido ou ultrapassado">
                            <WarningOutlined style={{ color: '#ff4d4f', marginLeft: 8 }} />
                        </Tooltip>
                    )}
                </div>
                <div style={{ margin: '12px 0' }}>
                    <DualCpfBar
                        usados={item.cpfs_usados || 0}
                        limite={item.limite_cpfs || 0}
                        passageiros={item.passageiros}
                    />
                </div>
                <div className="controle-cpfs-saldo-row">
                    <Text type="secondary">Saldo</Text>
                    <Text strong>{formatSaldo(item.saldo_atual)}</Text>
                </div>
                {item.politica_reset_cpf !== 'LISTA_BENEFICIARIOS' && (
                    <Space style={{ marginTop: 12 }} wrap>
                        <Button
                            type="default"
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={() => abrirModalInformarCpf(item)}
                        >
                            Informar CPF usado
                        </Button>
                        <Button
                            type="default"
                            size="small"
                            icon={<UnorderedListOutlined />}
                            onClick={() => abrirModalUsos(item)}
                        >
                            Usos informados
                        </Button>
                    </Space>
                )}
            </Card>
        );
    };

    return (
        <div style={{ padding: isMobile ? '16px' : '0' }}>
            {contextHolder}

            <div style={{ marginBottom: 20 }}>
                <Title level={2} style={{ margin: 0 }}>
                    <UserOutlined style={{ marginRight: 8 }} /> Controle de CPFs
                </Title>
                <Text type="secondary">
                    Acompanhe o uso de CPFs de terceiros e o saldo por pessoa em cada programa.
                </Text>
            </div>

            {cpfData.length > 0 && (
                <>
                    <Row gutter={[12, 12]} className="controle-cpfs-filters" style={{ marginBottom: 16 }}>
                        <Col xs={24} sm={8} md={7}>
                            <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Pessoa</Text>
                            <Select
                                style={{ width: '100%' }}
                                value={filtroPessoa}
                                onChange={setFiltroPessoa}
                                options={[{ value: 'todos', label: 'Todos' }, ...pessoasOptions]}
                            />
                        </Col>
                        <Col xs={24} sm={8} md={7}>
                            <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Programa</Text>
                            <Select
                                style={{ width: '100%' }}
                                value={filtroPrograma}
                                onChange={setFiltroPrograma}
                                options={[{ value: 'todos', label: 'Todos' }, ...programasOptions]}
                            />
                        </Col>
                    </Row>

                    <Row gutter={[12, 12]} className="controle-cpfs-summary" style={{ marginBottom: 24 }}>
                        <Col xs={12} sm={6}>
                            <Card size="small">
                                <Statistic title="CPFs usados" value={summary.usados} valueStyle={{ fontSize: '1.25rem' }} />
                            </Card>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Card size="small">
                                <Statistic title="CPFs disponíveis" value={summary.disponiveis} valueStyle={{ fontSize: '1.25rem', color: '#2F9E6F' }} />
                            </Card>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Card size="small">
                                <Statistic
                                    title="No limite / alerta"
                                    value={`${summary.noLimite} / ${summary.emAlerta}`}
                                    prefix={summary.noLimite > 0 ? <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} /> : <InfoCircleOutlined />}
                                    valueStyle={{ fontSize: '1.25rem' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Card size="small">
                                <Statistic title="Saldo total" value={formatSaldo(summary.saldoTotal)} valueStyle={{ fontSize: '1.25rem' }} />
                            </Card>
                        </Col>
                    </Row>
                </>
            )}

            {loading ? (
                <Card loading />
            ) : filteredData.length === 0 ? (
                cpfData.length === 0 ? emptyCpfs : (
                    <EmptyStateGuide
                        title="Nenhum resultado para os filtros."
                        description="Ajuste Pessoa ou Programa para ver as contas monitoradas."
                        actionLabel="Limpar filtros"
                        onAction={() => {
                            setFiltroPessoa('todos');
                            setFiltroPrograma('todos');
                        }}
                        icon={null}
                    />
                )
            ) : (
                groupedByPrograma.map(({ programa, items, sample }) => (
                    <section key={programa} className="controle-cpfs-programa-section">
                        <div className="controle-cpfs-programa-header">
                            <Title level={4} style={{ margin: 0 }}>{programa}</Title>
                            <Tag>{periodoDetalhe(sample)}</Tag>
                        </div>
                        {isMobile ? (
                            <List
                                dataSource={items}
                                renderItem={renderMobileItem}
                                split={false}
                            />
                        ) : (
                            <Table
                                columns={groupColumns}
                                dataSource={items}
                                rowKey="programa_portador_id"
                                pagination={false}
                                size="middle"
                                scroll={{ x: 640 }}
                            />
                        )}
                    </section>
                ))
            )}

            <Modal
                title="Informar CPF utilizado"
                open={modalInformarCpfVisible}
                onOk={handleSubmitInformarCpf}
                onCancel={fecharModalInformarCpf}
                okText="Registrar"
                cancelText="Cancelar"
                destroyOnClose
                width={isMobile ? '100%' : 520}
                style={isMobile ? { top: 0, margin: 0, maxWidth: '100%', paddingBottom: 0 } : {}}
                styles={isMobile ? { body: { maxHeight: 'calc(100dvh - 120px)', overflowY: 'auto' } } : undefined}
            >
                <p style={{ marginBottom: 16, color: '#666' }}>
                    Registre um CPF usado em emissão feita antes de usar o Saldo Ativo, para que a contagem reflita o uso real.
                </p>
                <Form form={form} layout="vertical" size={isMobile ? 'large' : 'middle'}>
                    <Form.Item name="cpf_desconhecido" valuePropName="checked" initialValue={false}>
                        <Checkbox>
                            Não tenho o CPF (reserva 1 vaga na contagem)
                        </Checkbox>
                    </Form.Item>
                    {cpfDesconhecidoWatch ? (
                        <>
                            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                                Use quando souber que houve emissão, mas não tiver o CPF. Cada registro consome 1 vaga.
                            </Text>
                            <Form.Item
                                name="nome_referencia"
                                label="Nome ou referência"
                                rules={[{ required: true, message: 'Informe o nome ou uma referência' }]}
                            >
                                <Input placeholder="Ex.: passageiro do dia 04/05" />
                            </Form.Item>
                        </>
                    ) : (
                        <>
                            <Form.Item
                                name="tipo_passageiro"
                                label="Passageiro"
                                initialValue="existente"
                                rules={[{ required: true }]}
                            >
                                <Select>
                                    <Select.Option value="existente">Selecionar existente</Select.Option>
                                    <Select.Option value="novo">Cadastrar novo</Select.Option>
                                </Select>
                            </Form.Item>
                            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.tipo_passageiro !== curr.tipo_passageiro}>
                                {({ getFieldValue }) =>
                                    getFieldValue('tipo_passageiro') === 'existente' ? (
                                        <Form.Item
                                            name="passageiro_id"
                                            label="Passageiro"
                                            rules={[{ required: true, message: 'Selecione o passageiro' }]}
                                        >
                                            <Select
                                                placeholder="Selecione o passageiro"
                                                showSearch
                                                optionFilterProp="label"
                                                options={passageiros.map((p) => ({
                                                    value: p.id,
                                                    label: `${p.nome_completo} (${p.cpf})`,
                                                }))}
                                            />
                                        </Form.Item>
                                    ) : (
                                        <>
                                            <Form.Item
                                                name="nome_completo"
                                                label="Nome completo"
                                                rules={[{ required: true, message: 'Informe o nome' }]}
                                            >
                                                <Input placeholder="Nome do passageiro" />
                                            </Form.Item>
                                            <Form.Item
                                                name="cpf"
                                                label="CPF"
                                                rules={[
                                                    { required: true, message: 'Informe o CPF' },
                                                    {
                                                        validator: (_, value) => {
                                                            const digits = (value || '').replace(/\D/g, '');
                                                            if (digits.length === 11) return Promise.resolve();
                                                            return Promise.reject(new Error('CPF deve ter 11 dígitos'));
                                                        },
                                                    },
                                                ]}
                                            >
                                                <Input placeholder="Apenas números (11 dígitos)" maxLength={14} />
                                            </Form.Item>
                                        </>
                                    )
                                }
                            </Form.Item>
                        </>
                    )}
                    <Form.Item
                        name="data_emissao"
                        label="Data da emissão"
                        rules={[{ required: true, message: 'Informe a data' }]}
                    >
                        <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                    </Form.Item>
                    <Form.Item name="observacao" label="Observação">
                        <Input.TextArea rows={2} placeholder="Opcional" />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={
                    recordUsos
                        ? `Usos informados — ${recordUsos.portador_nome} · ${recordUsos.programa_nome}`
                        : 'Usos informados'
                }
                open={modalUsosVisible}
                onCancel={fecharModalUsos}
                footer={[
                    <Button key="close" onClick={fecharModalUsos}>Fechar</Button>,
                ]}
                destroyOnClose
                width={isMobile ? '100%' : 560}
            >
                <List
                    loading={loadingUsos}
                    locale={{ emptyText: 'Nenhum uso informado neste programa.' }}
                    dataSource={usosInformados}
                    renderItem={(uso) => (
                        <List.Item
                            actions={[
                                <Popconfirm
                                    key="del"
                                    title="Remover este registro?"
                                    okText="Remover"
                                    cancelText="Cancelar"
                                    onConfirm={() => handleExcluirUso(uso.id)}
                                >
                                    <Button type="link" danger size="small" icon={<DeleteOutlined />}>
                                        Excluir
                                    </Button>
                                </Popconfirm>,
                            ]}
                        >
                            <List.Item.Meta
                                title={
                                    uso.cpf_desconhecido
                                        ? (uso.nome_referencia || uso.passageiro_nome || 'CPF não informado')
                                        : (uso.passageiro_nome || 'Passageiro')
                                }
                                description={
                                    <Space direction="vertical" size={0}>
                                        <Text type="secondary">
                                            {uso.cpf_desconhecido
                                                ? 'CPF não informado'
                                                : maskCpf(uso.passageiro_cpf)}
                                            {' · '}
                                            {uso.data_emissao
                                                ? dayjs(uso.data_emissao).format('DD/MM/YYYY')
                                                : '—'}
                                        </Text>
                                        {uso.observacao ? (
                                            <Text type="secondary" style={{ fontSize: 12 }}>{uso.observacao}</Text>
                                        ) : null}
                                    </Space>
                                }
                            />
                        </List.Item>
                    )}
                />
            </Modal>
        </div>
    );
};

export default ControleCpfsPage;
